<template>
  <div class="site-shell">
    <SiteHeader
      :active-tab="activeTab"
      :tabs="tabs"
      @change-tab="changeTab"
      @mobile-menu-toggle="handleMobileMenuToggle"
    />

    <main :class="['main-content', { 'menu-open': mobileMenuExpanded }]">
      <transition name="tab-fade" mode="out-in">
        <component :is="activeComponent" :key="activeTab" @jump-tab="changeTab" />
      </transition>
    </main>

    <SiteFooter
      :active-tab="activeTab"
      :tabs="tabs"
      @change-tab="changeTab"
    />
  </div>
</template>

<script>
import SiteHeader from './SiteHeader.vue'
import SiteFooter from './SiteFooter.vue'
import HomeTab from '../tabs/HomeTab.vue'
import AboutTab from '../tabs/AboutTab.vue'
import ProductsTab from '../tabs/ProductsTab.vue'
import JoinTab from '../tabs/JoinTab.vue'
import ContactTab from '../tabs/ContactTab.vue'
import PrivacyPolicyTab from '../tabs/PrivacyPolicyTab.vue'

const TAB_COMPONENT_MAP = {
  home: HomeTab,
  about: AboutTab,
  products: ProductsTab,
  join: JoinTab,
  contact: ContactTab,
  privacy: PrivacyPolicyTab
}

export default {
  name: 'SiteShell',
  components: {
    SiteHeader,
    SiteFooter,
    HomeTab,
    AboutTab,
    ProductsTab,
    JoinTab,
    ContactTab,
    PrivacyPolicyTab
  },
  data() {
    return {
      activeTab: 'home',
      tabs: [
        { key: 'home', label: '首页' },
        { key: 'about', label: '关于我们' },
        { key: 'products', label: '产品服务' },
        { key: 'join', label: '加入我们' },
        { key: 'contact', label: '联系我们' }
      ],
      observer: null,
      mobileMenuExpanded: false
    }
  },
  computed: {
    activeComponent() {
      return TAB_COMPONENT_MAP[this.activeTab] || HomeTab
    }
  },
  mounted() {
    this.activateFromHash()
    this.updateDocumentTitle(this.activeTab)
    this.$nextTick(this.bindRevealObserver)
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },
  methods: {
    updateDocumentTitle(tabKey) {
      const titlePrefix = '中鸿-'
      const titleMap = this.tabs.reduce((map, item) => {
        map[item.key] = item.label
        return map
      }, { privacy: '隐私政策' })

      const pageTitle = titleMap[tabKey] || titleMap.home || '首页'
      document.title = `${titlePrefix}${pageTitle}`
    },
    changeTab(tabKey) {
      if (!TAB_COMPONENT_MAP[tabKey]) {
        this.activeTab = 'home'
        window.location.hash = 'home'
        this.updateDocumentTitle('home')
        return
      }

      if (tabKey === this.activeTab) {
        this.updateDocumentTitle(tabKey)
        return
      }
      this.activeTab = tabKey
      this.updateDocumentTitle(tabKey)
      this.mobileMenuExpanded = false
      window.location.hash = tabKey
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setTimeout(() => {
        this.bindRevealObserver()
      }, 320)
    },
    handleMobileMenuToggle(expanded) {
      this.mobileMenuExpanded = expanded
    },
    activateFromHash() {
      const hash = window.location.hash.replace('#', '')
      const hasTab = Boolean(TAB_COMPONENT_MAP[hash])
      if (hasTab) {
        this.activeTab = hash
        this.updateDocumentTitle(hash)
      } else {
        this.updateDocumentTitle('home')
      }
    },
    bindRevealObserver() {
      const items = this.$el.querySelectorAll('.reveal')

      if (this.observer) {
        this.observer.disconnect()
      }

      if (!items.length) return

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.remove('reveal-pending')
              entry.target.classList.add('in-view')
            }
          })
        },
        {
          threshold: 0.2
        }
      )

      items.forEach((item) => {
        item.classList.remove('in-view')
        item.classList.add('reveal-pending')
        this.observer.observe(item)

        const { top } = item.getBoundingClientRect()
        if (top < window.innerHeight * 0.95) {
          item.classList.remove('reveal-pending')
          item.classList.add('in-view')
        }
      })
    }
  }
}
</script>

<style scoped lang="less">
.site-shell {
  min-height: 100vh;
}

.main-content {
  padding-top: 88px;
}

@media (max-width: 1024px) {
  .main-content {
    padding-top: 80px;
  }
}

@media (max-width: 768px) {
  .main-content {
    padding-top: 72px;
  }

  .main-content.menu-open {
    padding-top: 167px;
  }
}
</style>
